import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { appointmentsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

/**
 * The four takeover lifecycle mutations: mark / unmark for takeover, take over, and reverse a
 * takeover. Each invalidates both the takeover board and the appointment calendar (take-over
 * reassigns ownership, so the calendar changes too).
 */
export function useTakeoverMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.AppointmentTakeovers] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
  };

  // Bulk actions fan out one request per id. Promise.allSettled keeps a single failure from
  // aborting its siblings; if any request rejected we rethrow so the success toast is suppressed
  // and onError re-syncs the board. The individual error messages are surfaced by the global
  // errorMiddleware, so we don't show them again here.
  const runBulk = async (ids: string[], operation: (id: string) => Promise<unknown>) => {
    const results = await Promise.allSettled(ids.map(operation));
    if (results.some((result) => result.status === "rejected")) {
      throw new Error("One or more takeover operations failed.");
    }
  };

  const mark = useMutation({
    mutationFn: (id: string) => appointmentsApi.markAppointmentForTakeover({ id }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.marked"));
    },
    onError: invalidate,
  });

  const unmark = useMutation({
    mutationFn: (id: string) => appointmentsApi.unmarkAppointmentFromTakeover({ id }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.unmarked"));
    },
    onError: invalidate,
  });

  const markMany = useMutation({
    mutationFn: (ids: string[]) => runBulk(ids, (id) => appointmentsApi.markAppointmentForTakeover({ id })),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.marked"));
    },
    onError: invalidate,
  });

  const unmarkMany = useMutation({
    mutationFn: (ids: string[]) => runBulk(ids, (id) => appointmentsApi.unmarkAppointmentFromTakeover({ id })),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.unmarked"));
    },
    onError: invalidate,
  });

  const reverseMany = useMutation({
    mutationFn: (ids: string[]) => runBulk(ids, (id) => appointmentsApi.reverseAppointmentTakeover({ id })),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.reversed"));
    },
    onError: invalidate,
  });

  const takeOver = useMutation({
    mutationFn: (id: string) => appointmentsApi.takeOverAppointment({ id }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.takenOver"));
    },
    onError: invalidate,
  });

  const reverse = useMutation({
    mutationFn: (id: string) => appointmentsApi.reverseAppointmentTakeover({ id }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("takeover.messages.reversed"));
    },
    onError: invalidate,
  });

  return { mark, unmark, markMany, unmarkMany, reverseMany, takeOver, reverse };
}
